import { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { PMPagination } from "../../../utils/PMPagination";
import { Link, useNavigate } from "react-router-dom";
import { CopyToClipboard } from "../../../utils/Div";
import { ResourceEmpty } from "../../../utils/ResourceEmpty";
import { resendInvitation, sendPasswordSetup } from "../../../../api/user";
import { toast } from "react-hot-toast";
import {
  PencilIcon,
  UserIcon,
  CalendarIcon,
  PhoneIcon,
  EnvelopeIcon,
  Cog6ToothIcon,
  EyeIcon,
  PaperAirplaneIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";

// Deterministic avatar color per entity (brand design system §2)
const AVATAR_COLORS = [
  "#4F46E5",
  "#0D9488",
  "#D946EF",
  "#F59E0B",
  "#0EA5E9",
  "#10B981",
  "#1463C7",
];
const avatarColor = (key) => {
  const s = String(key ?? "");
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};
const initialsOf = (name) =>
  String(name || "")
    .split(" ")
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

export const UsersTable = (props) => {
  const { t } = useTranslation();
  const {
    itemState,
    onPageChange,
    itemsCount,
    currentCount,
    page,
    perPage,
    onItemsSelect,
    onInvitationResent,
  } = props;
  const { data, isLoading, errors } = itemState;
  const users = data;
  const checkbox = useRef();
  const [checked, setChecked] = useState(false);
  const [indeterminate, setIndeterminate] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [resendingUserId, setResendingUserId] = useState(null);

  const handleResendInvitation = async (user) => {
    if (!user?.id || resendingUserId) return;

    setResendingUserId(user.id);
    try {
      // Prefer send_password_setup: works without a prior invitation (Tripletex orphans).
      const response = user.can_send_password_setup
        ? await sendPasswordSetup(user.id)
        : await resendInvitation(user.id);
      if (response?.errors?.length) {
        toast.error(response.errors.join(", "));
        return;
      }

      toast.success(
        response?.message || t("company.users.invitation.resend_success")
      );
      onInvitationResent?.();
    } catch (error) {
      const errorMessage =
        error?.errors?.join?.(", ") ||
        error?.response?.data?.errors?.join(", ") ||
        t("company.users.invitation.resend_error");
      toast.error(errorMessage);
    } finally {
      setResendingUserId(null);
    }
  };

  useLayoutEffect(() => {
    const isIndeterminate =
      selectedItems.length > 0 && selectedItems.length < users?.length;
    setChecked(selectedItems.length === users?.length);
    setIndeterminate(isIndeterminate);
    if (checkbox.current) {
      checkbox.current.indeterminate = isIndeterminate;
    }
    onItemsSelect(selectedItems);
  }, [selectedItems, users?.length, onItemsSelect]);

  function toggleAll() {
    setSelectedItems(checked || indeterminate ? [] : users || []);
    setChecked(!checked && !indeterminate);
    setIndeterminate(false);
  }

  const navigate = useNavigate();

  const getUserStatus = (user) => {
    if (!user.active)
      return {
        label: t("company.users.table.status_inactive"),
        cls: "bg-[#FEF2F2] text-[#DC2626]",
        dot: "#DC2626",
      };
    if (!user.approved)
      return {
        label: t("company.users.table.status_pending"),
        cls: "bg-[#FBEFD9] text-[#C2710C]",
        dot: "#C2710C",
      };
    return {
      label: t("company.users.table.status_active"),
      cls: "bg-[#E2F3E8] text-[#15924E]",
      dot: "#15924E",
    };
  };

  const getUserRoles = (user) => {
    if (!user.roles || user.roles.length === 0) return [];
    return user.roles.map((role) => role.name);
  };

  // Reasons a user won't appear in the employee/resource selectors (and other
  // missing setup). Helps explain why e.g. an innleid user is not visible.
  const getVisibilityWarnings = (user) => {
    const reasons = [];
    const isHired = user?.user_type_id === 3;
    if (!user?.active) reasons.push(t("company.users.visibility.inactive"));
    if (!user?.show_in_dropdown)
      reasons.push(t("company.users.visibility.not_in_dropdown"));
    if (isHired && !user?.hired_employee_supplier_id)
      reasons.push(t("company.users.visibility.missing_supplier"));
    if (!isHired && !user?.employment)
      reasons.push(t("company.users.visibility.not_employed"));
    if (!user?.roles || user.roles.length === 0)
      reasons.push(t("company.users.visibility.no_roles"));
    return reasons;
  };

  const rowBtn =
    "inline-flex items-center gap-1.5 rounded-[9px] border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60";
  const rowBtnAccent =
    "inline-flex items-center gap-1.5 rounded-[9px] border border-[#1463C7] bg-white px-2.5 py-1.5 text-xs font-bold text-[#1463C7] transition hover:bg-[#EAF3FD]";

  function renderUserRows() {
    if (!users || users.length === 0) {
      return (
        <div className="px-6 py-14 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-[12px] bg-[#EAF3FD] text-[#1463C7]">
            <UserIcon className="h-6 w-6" />
          </div>
          <h3 className="mt-3 text-sm font-bold text-slate-900">
            {t("company.users.table.empty_title")}
          </h3>
          <p className="mt-1 text-sm text-slate-500">
            {t("company.users.table.empty_hint")}
          </p>
        </div>
      );
    }

    return users.map((user) => {
      const status = getUserStatus(user);
      const roles = getUserRoles(user);
      const visibilityWarnings = getVisibilityWarnings(user);
      const selected = selectedItems.includes(user);
      const color = avatarColor(user?.id || user?.name);
      const initials = initialsOf(user?.name);

      return (
        <div
          key={user.id}
          className={`flex flex-col gap-3 px-5 py-3.5 transition hover:bg-slate-50 sm:flex-row sm:items-center sm:gap-4 ${
            selected ? "bg-[#EAF3FD]/50" : ""
          }`}
        >
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <input
              type="checkbox"
              className="h-4 w-4 flex-shrink-0 rounded border-slate-300 text-[#1463C7] focus:ring-[#1463C7]"
              value={user.id}
              checked={selected}
              onChange={(e) =>
                setSelectedItems(
                  e.target.checked
                    ? [...selectedItems, user]
                    : selectedItems.filter((p) => p !== user)
                )
              }
            />
            <Link
              to={"/dashboard/users/" + user?.id}
              className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold text-white"
              style={{ backgroundColor: color }}
            >
              {initials || <UserIcon className="h-5 w-5" />}
            </Link>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <Link
                  to={"/dashboard/users/" + user?.id}
                  className="truncate text-sm font-bold text-slate-900 transition hover:text-[#1463C7]"
                >
                  {user?.name || t("company.users.table.unnamed")}
                </Link>
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-bold ${status.cls}`}
                >
                  <span
                    className="h-1.5 w-1.5 rounded-full"
                    style={{ backgroundColor: status.dot }}
                  />
                  {status.label}
                </span>
                {user?.user_type_id === 3 ? (
                  <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-bold text-amber-800">
                    {t("employeeSelector.hired_badge")}
                  </span>
                ) : null}
                {visibilityWarnings.length > 0 ? (
                  <span
                    className="inline-flex items-center gap-1 rounded-full bg-[#FBEFD9] px-2.5 py-0.5 text-xs font-bold text-[#C2710C]"
                    title={`${t("company.users.visibility.tag_hint")}: ${visibilityWarnings.join(", ")}`}
                  >
                    <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                    {t("company.users.visibility.tag")}
                  </span>
                ) : null}
              </div>

              <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-500">
                {user?.email ? (
                  <span className="flex min-w-0 items-center gap-1">
                    <EnvelopeIcon className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
                    <Link
                      to={"mailto:" + user?.email}
                      className="truncate font-medium text-slate-600 hover:text-[#1463C7]"
                    >
                      {user?.email}
                    </Link>
                  </span>
                ) : null}
                {user?.mobile ? (
                  <span className="flex items-center gap-1">
                    <PhoneIcon className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
                    <CopyToClipboard path={null} content={user?.mobile} type="popup" />
                  </span>
                ) : null}
                {roles.slice(0, 3).map((role, index) => (
                  <span
                    key={index}
                    className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600"
                  >
                    {role}
                  </span>
                ))}
                {roles.length > 3 ? (
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                    +{roles.length - 3}
                  </span>
                ) : null}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-1.5 pl-[52px] sm:flex-shrink-0 sm:justify-end sm:pl-0">
            {user.can_resend_invitation || user.can_send_password_setup ? (
              <button
                type="button"
                onClick={() => handleResendInvitation(user)}
                disabled={resendingUserId === user.id}
                className={rowBtn}
              >
                <PaperAirplaneIcon className="h-3.5 w-3.5" />
                {resendingUserId === user.id
                  ? t("company.users.invitation.resending")
                  : user.can_resend_invitation
                    ? t("company.users.invitation.resend_button")
                    : t("company.users.password.send_link")}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => {
                const today = new Date().toISOString().split("T")[0];
                window.open(
                  `/dashboard/calendar?date=${today}&view=day&users=${user.id}&showWeekNumbers=false`,
                  "_blank"
                );
              }}
              className={rowBtn}
            >
              <CalendarIcon className="h-3.5 w-3.5" />
              {t("company.users.table.calendar")}
            </button>
            <button
              type="button"
              onClick={() => navigate("/dashboard/users/" + user.id)}
              className={rowBtn}
            >
              <Cog6ToothIcon className="h-3.5 w-3.5" />
              {t("company.users.table.settings")}
            </button>
            <button
              type="button"
              onClick={() => navigate("/dashboard/users/" + user.id)}
              className={rowBtn}
            >
              <EyeIcon className="h-3.5 w-3.5" />
              {t("company.users.table.view")}
            </button>
            <button
              type="button"
              onClick={() => navigate("/dashboard/users/" + user.id)}
              className={rowBtnAccent}
            >
              <PencilIcon className="h-3.5 w-3.5" />
              {t("company.users.table.edit")}
            </button>
          </div>
        </div>
      );
    });
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 px-6 py-16">
        <div
          className="h-10 w-10 animate-spin rounded-full border-2 border-slate-200 border-t-[#1463C7]"
          aria-hidden
        />
        <p className="text-sm text-slate-500">{t("company.users.loading")}</p>
      </div>
    );
  }

  return (
    <div>
      {users && users.length > 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#EEF1F5] bg-slate-50 px-5 py-3">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-[#1463C7] focus:ring-[#1463C7]"
              ref={checkbox}
              checked={checked}
              onChange={toggleAll}
            />
            <span className="text-xs font-semibold text-slate-500">
              {selectedItems.length > 0
                ? t("company.users.table.selected_of_total", {
                    selected: selectedItems.length,
                    total: users.length,
                  })
                : t("company.users.table.user_count", { count: users.length })}
            </span>
          </div>
          {selectedItems.length > 0 ? (
            <span className="inline-flex items-center rounded-full bg-[#EAF3FD] px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-[0.14em] text-[#1463C7]">
              {t("company.users.table.n_selected", {
                count: selectedItems.length,
              })}
            </span>
          ) : null}
        </div>
      ) : null}

      <div className="divide-y divide-[#EEF1F5]">{renderUserRows()}</div>

      {Array.isArray(errors) && errors.length > 0 ? (
        <div className="border-t border-[#EEF1F5] px-5 py-4 text-sm text-[#DC2626]">
          {errors.join(" ")}
        </div>
      ) : null}

      <div className="border-t border-[#EEF1F5] px-5 py-4">
        <PMPagination
          disabled={isLoading}
          onPageChange={onPageChange}
          page={page}
          perPage={perPage}
          totalCount={itemsCount}
          currentCount={currentCount}
          showCount={true}
        />
      </div>
    </div>
  );
};

UsersTable.defaultProps = {
  page: 1,
  itemState: [],
  itemsCount: 0,
  selectedItems: [],
  sort: "desc",
  sortBy: "createdAt",
};
